
import React, { useState, useEffect } from 'react';
// Added missing import for useNavigate
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Zap, Shield, Target, ChevronRight, Terminal, BarChart3, CheckCircle2, Lock } from 'lucide-react';
import { Button, Card, Badge } from './UI';
import { useStore } from '../store/useStore';
import { toast } from 'sonner';

export const AdminConsole: React.FC = () => {
  // Initialize navigate hook to handle navigation
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [docType, setDocType] = useState('');
  const [objective, setObjective] = useState('');
  const [strictness, setStrictness] = useState('Balanced');
  const [isProcessing, setIsProcessing] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const { completeMission, updateReadiness, mission } = useStore();

  const handleNext = () => setStep(s => s + 1);

  const startProcessing = async () => {
    setIsProcessing(true);
    setLogs([]);
    const lines = [
      `Initializing Agent Layer for ${docType.toUpperCase()}...`,
      `Processing goal: ${objective.toUpperCase()}`,
      `Policy strictness: ${strictness.toUpperCase()}`,
      `[Agent-01] Observing document layout...`,
      `[Agent-01] Arabic characters detected (Conf: 0.998)...`,
      `[Agent-02] Extracting business-critical entities...`,
      `[Agent-02] Cross-referencing entities with on-prem database...`,
      `[Agent-03] Running compliance check against ${strictness} policy...`,
      `Validation Complete.`,
      `Logging processing results...`,
      `READINESS_SCORE_GENERATED`
    ];

    for (const line of lines) {
      setLogs(prev => [...prev, line]);
      await new Promise(r => setTimeout(r, 400));
    }

    const score = Math.floor(Math.random() * 20) + 80;
    updateReadiness(score);
    completeMission('MISSION_COMPLETE');
    setIsProcessing(false);
    toast.success('Processing Complete!', { description: `Readiness Score: ${score}%` });
    setStep(4);
  };

  return (
    <section id="mission-console" className="py-24 px-8 max-w-7xl mx-auto">
      <div className="flex flex-col lg:flex-row gap-12">
        {/* Left: Mission Selector */}
        <div className="lg:w-1/3 space-y-8">
          <div className="space-y-4">
            <h2 className="text-4xl font-black tracking-tight text-white leading-tight">LensVerse Admin Console</h2>
            <p className="text-white/40 font-medium">Complete these steps to unlock your enterprise readiness report.</p>
          </div>

          <div className="space-y-4 relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-white/5 -z-10" />
            {[1, 2, 3].map((s) => (
              <div key={s} className={cn(
                "flex items-center gap-6 p-4 rounded-2xl transition-all",
                step === s ? "bg-white/5 border border-white/10 shadow-[0_0_20px_rgba(247,108,28,0.1)]" : "opacity-30"
              )}>
                <div className={cn(
                  "w-12 h-12 rounded-xl flex items-center justify-center font-black transition-all",
                  step > s ? "bg-green-500 text-deep-0" : step === s ? "bg-accent text-deep-0" : "bg-deep-surface border border-white/10"
                )}>
                  {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-white/40">Step 0{s}</p>
                  <p className="font-bold text-white">
                    {s === 1 ? 'Target Document' : s === 2 ? 'Processing Goal' : 'Policy Strictness'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Interactive Display */}
        <Card className="flex-1 p-8 lg:p-12 border-white/5 bg-deep-1/50 relative overflow-hidden flex flex-col min-h-[500px]">
          <div className="absolute top-0 left-0 w-full h-1 bg-accent/20" />

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="st1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-10 flex-1 flex flex-col justify-center">
                <div className="text-center space-y-2">
                  <FileText className="w-12 h-12 text-accent mx-auto mb-4" />
                  <h3 className="text-2xl font-black text-white">Select Document Type</h3>
                  <p className="text-white/40">Which document should we simulate?</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {['KYC Doc', 'Commercial Contract', 'Supplier Invoice', 'Loan Agreement'].map(type => (
                    <button
                      key={type}
                      onClick={() => { setDocType(type); handleNext(); }}
                      className="p-6 rounded-2xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-accent/40 transition-all text-center group"
                    >
                      <p className="font-bold text-white group-hover:text-accent">{type}</p>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="st2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-10 flex-1 flex flex-col justify-center">
                <div className="text-center space-y-2">
                  <Target className="w-12 h-12 text-accent mx-auto mb-4" />
                  <h3 className="text-2xl font-black text-white">Define Processing Goal</h3>
                  <p className="text-white/40">What is the outcome of this automation?</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {['Extract & Map', 'Validate & Audit', 'PII Redaction', 'Arabic Translation'].map(goal => (
                    <button
                      key={goal}
                      onClick={() => { setObjective(goal); handleNext(); }}
                      className="p-6 rounded-2xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-accent/40 transition-all text-center group"
                    >
                      <p className="font-bold text-white group-hover:text-accent">{goal}</p>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="st3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-10 flex-1 flex flex-col justify-center">
                <div className="text-center space-y-2">
                  <Shield className="w-12 h-12 text-accent mx-auto mb-4" />
                  <h3 className="text-2xl font-black text-white">Set Policy Protocol</h3>
                  <p className="text-white/40">Choose how strictly agents should flag anomalies.</p>
                </div>
                <div className="flex gap-4">
                  {['Conservative', 'Balanced', 'Aggressive'].map(s => (
                    <button
                      key={s}
                      onClick={() => setStrictness(s)}
                      className={cn(
                        "flex-1 p-6 rounded-2xl border transition-all text-center",
                        strictness === s ? "border-accent bg-accent/10 text-white" : "border-white/5 bg-white/[0.02] text-white/40"
                      )}
                    >
                      <p className="font-black text-sm uppercase tracking-widest">{s}</p>
                    </button>
                  ))}
                </div>
                <Button size="xl" className="w-full" isLoading={isProcessing} onClick={startProcessing}>
                  Start Processing <Zap className="ml-3 w-5 h-5" />
                </Button>

                {isProcessing && (
                  <div className="bg-black/40 p-4 rounded-xl font-mono text-[10px] space-y-1 h-32 overflow-y-auto border border-white/5">
                    {logs.map((line, i) => (
                      <motion.div key={i} initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }}>
                        <span className="text-accent/50 mr-2">{'>>>'}</span> {line}
                      </motion.div>
                    ))}
                    <motion.span animate={{ opacity: [1, 0] }} transition={{ repeat: Infinity, duration: 0.8 }} className="inline-block w-2 h-3 bg-accent ml-1" />
                  </div>
                )}
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="st4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-8 flex-1 flex flex-col justify-center items-center text-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-accent/20 blur-3xl rounded-full" />
                  <div className="w-32 h-32 rounded-full border-4 border-accent border-t-transparent animate-spin relative" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-black text-white">{mission.readinessScore}%</span>
                    <span className="text-[8px] font-bold text-white/40 uppercase tracking-widest">Readiness</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-3xl font-black text-white italic">Intelligence Unlocked.</h3>
                  <p className="text-white/40 max-w-md mx-auto">Your configuration is optimized for {docType} with {strictness} compliance standards.</p>
                </div>

                <div className="flex gap-4">
                  {mission.unlockedBadges.map(b => (
                    <Badge key={b} variant="accent" className="animate-bounce">{b}</Badge>
                  ))}
                </div>

                <div className="flex gap-4 w-full mt-8">
                  <Button
                    variant="outline"
                    className="flex-1 border-white/20 text-white/60 hover:bg-white/10 hover:text-white hover:border-white/40 transition-all duration-300"
                    onClick={() => setStep(1)}
                  >
                    Reset Console
                  </Button>

                  <Button
                    className="flex-[2] shadow-[0_0_20px_rgba(247,108,28,0.3)] hover:shadow-[0_0_30px_rgba(247,108,28,0.5)] transition-all"
                    onClick={() => navigate('/auth/signup')}
                  >
                    Request Full Audit
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      </div>
    </section>
  );
};

const cn = (...classes: any[]) => classes.filter(Boolean).join(' ');
